import { Component, h, State, Listen, Prop, Element, EventEmitter, Event } from '@stencil/core';
import { NavItem } from '../aafp-nav-item/aafp-nav-item';
import { SecondaryNavItem } from '../aafp-secondary-nav/aafp-secondary-nav';
import { getClassListString, showFoundationStyles } from '../../component-utils/stencilUtils';

export declare interface GlobalNavItem extends NavItem {
  secondaryNavLinks?: SecondaryNavItem[];
  flyoutText: string;
  flyoutImage: string;
  flyoutCtaText: string;
  flyoutCtaLink: string;
  isCurrentSection: boolean;
}

@Component({
  tag: 'aafp-global-nav',
  shadow: false,
})
export class AafpGlobalNav {
  windowWidth: number;
  @Prop({ mutable: true }) brandImagePath: string = '';
  @Prop({ mutable: true }) brandLinkUrl: string = '';
  @Prop({ mutable: true }) brandLabel: string = '';
  @Prop({ mutable: true }) brandNavItems: string = '[]';
  @Prop({ mutable: true }) brandNavItemElements: HTMLCollection | null = null;
  @Prop({ mutable: true }) notSimple: boolean = true;
  @Element() element: HTMLElement;
  @Event() didLoad: EventEmitter;
  @State() mobileMenuIsOpen: boolean = false;
  brandCtaPlaceholder: HTMLElement;
  globalMenuItems: GlobalNavItem[];
  hasSearchSlot: boolean;

  @Listen('resize', { target: 'window' })
  handleResize(event: UIEvent) {
    if (!event.target) return;
    const windowRef = event.target as Window;
    const width = windowRef.innerWidth;
    if (this.windowWidth === width) return;
    this.windowWidth = width;
    this.toggleMobileMenu(true);
  }

  didLoadHandler() {
    const navList = this.element.querySelector('ul');
    this.didLoad.emit(navList);
  }

  componentWillLoad() {
    this.brandNavItemElements = this.element.querySelectorAll(
      'aafp-nav-item[nav-type="global"]',
    ) as unknown as HTMLCollection;

    this.globalMenuItems = JSON.parse(this.brandNavItems);
    this.hasSearchSlot = !!this.element.querySelector('[slot="global-search"]');
  }

  componentDidLoad() {
    this.windowWidth = window.innerWidth;
    this.didLoadHandler();
  }

  resetAnimation() {
    // The following line allows the fade animations to retrigger.
    // Source: https://css-tricks.com/restart-css-animation/
    this.element.offsetWidth;
  }

  resetNav(submenu: HTMLElement) {
    Array.from(document.getElementsByClassName('adl-nav-item--focus')).forEach((element) => {
      element.classList.remove('adl-nav-item--focus');
    });
    submenu.classList.remove('adl-secondary-nav__fadein');
    this.resetAnimation();
    submenu.classList.add('adl-secondary-nav__fadeout');
  }

  stringifyFlyoutData = (flyoutImage: string, flyoutText: string, flyoutCtaText: string, flyoutCtaLink: string) => {
    return JSON.stringify({
      imgUrl: flyoutImage,
      text: flyoutText,
      linkText: flyoutCtaText,
      linkUrl: flyoutCtaLink,
    });
  };

  ACTIVE_GLOBAL_NAV_CLASS: string = 'adl-universal-nav__link--active';

  /**
   *
   * @param currentlySelected
   */
  isCurrentlySelected(currentlySelected: boolean): string {
    if (currentlySelected) {
      return this.ACTIVE_GLOBAL_NAV_CLASS;
    }
    return '';
  }

  renderBrandCtaPlaceholder(donateCta: HTMLElement) {
    const appendChild = (div: HTMLElement) => {
      if (!div.firstChild) {
        div.appendChild(donateCta.cloneNode(true));
      }
    };
    return (
      <div
        class="adl-global-nav__brand-cta--mobile"
        ref={(el: HTMLElement | undefined) => appendChild(el as HTMLElement)}
      ></div>
    );
  }

  hasSecondaryLinks(globalNavItem: GlobalNavItem): boolean {
    return !!(globalNavItem.secondaryNavLinks && globalNavItem.secondaryNavLinks.length);
  }

  render() {
    return (
      <nav
        class={getClassListString(
          {
            ['adl-space__sm__p-l-md']: !this.notSimple,
            ['adl-foundation-global-nav']: showFoundationStyles(document),
          },
          'adl-global-nav',
        )}
      >
        <a
          class={`${this.mobileMenuIsOpen ? 'adl-global-nav__top-item-menu--open ' : ''}adl-global-nav__brand-logo`}
          href={this.brandLinkUrl}
          aria-label={this.brandLabel}
        >
          <img class="adl-global-nav__brand-logo-image" src={this.brandImagePath} alt="brand logo" />
        </a>

        {this.element.hasAttribute('can-render-brand-cta') &&
          this.renderBrandCtaPlaceholder(this.element.querySelector('aafp-donate-button') as HTMLElement)}

        {this.notSimple ? (
          <button class="adl-global-nav__menu-toggle" onClick={() => this.toggleMobileMenu(this.mobileMenuIsOpen)}>
            <span class="adl-layout__sm-hide">Menu</span>
            <i class="adl-global-nav__menu-toggle-icon">{this.mobileMenuIsOpen ? 'close' : 'menu'}</i>
          </button>
        ) : (
          ''
        )}

        {this.hasSearchSlot ? (
          <button class="adl-global-nav__menu-toggle" onClick={() => this.focusMobileSearch()}>
            <span class="adl-layout__sm-hide">Search</span>
            <i class="adl-search__search-icon"> search </i>
          </button>
        ) : (
          ''
        )}

        <div
          class={`${this.mobileMenuIsOpen ? 'adl-global-nav__top-item-menu--open ' : ''}adl-global-nav__top-item-menu`}
        >
          <ul class="adl-global-nav__list">
            {this.globalMenuItems.map((globalNavItem: GlobalNavItem, index: number) => (
              <li class="adl-global-nav__list-item" aria-haspopup="true" aria-expanded="false" key={globalNavItem.text}>
                <aafp-nav-item
                  class={getClassListString(
                    { 'adl-nav-item__submenu': this.hasSecondaryLinks(globalNavItem) },
                    'adl-global-nav__nav-item',
                  )}
                  data-mobile-menu={this.mobileMenuIsOpen ? 'true' : 'false'}
                  nav-items={
                    this.hasSecondaryLinks(globalNavItem) ? JSON.stringify(globalNavItem.secondaryNavLinks) : '[]'
                  }
                  nav-index={`${index}`}
                  nav-type="global"
                  nav-title={globalNavItem.text}
                  nav-link={globalNavItem.link}
                  nav-flyout={this.stringifyFlyoutData(
                    globalNavItem.flyoutImage,
                    globalNavItem.flyoutText,
                    globalNavItem.flyoutCtaText,
                    globalNavItem.flyoutCtaLink,
                  )}
                >
                  <a
                    slot="nav-link"
                    class={`adl-global-nav__link ${this.isCurrentlySelected(globalNavItem.isCurrentSection)}`}
                    href={this.hasSecondaryLinks(globalNavItem) ? undefined : globalNavItem.link}
                  >
                    <span class="adl-global-nav__link-content">
                      <i class="adl-flyout-nav__link--chevron-back">chevron_left</i>
                      {globalNavItem.text}
                      <i class="adl-global-nav__link--chevron">chevron_right</i>
                    </span>
                  </a>
                </aafp-nav-item>
              </li>
            ))}
          </ul>

          {this.element.hasAttribute('can-render-brand-cta') && <slot name="brand-cta"></slot>}

          <div class="adl-global-nav__signin-content">
            <slot name="signin-content"></slot>
          </div>

          <div class="adl-global-nav__search-wrapper">
            <slot name="global-search"></slot>
          </div>
        </div>
      </nav>
    );
  }

  focusMobileSearch() {
    this.toggleMobileMenu(false);
    setTimeout(() => {
      // Wait for menu to open
      const search__input = document.querySelector('.adl-search__input') as HTMLInputElement;
      if (search__input) {
        search__input.focus();
      }
    }, 50);
  }

  toggleMobileMenu(isOpen: boolean) {
    // Checks for residual open navItems and resets the mobile menu
    if (isOpen) {
      const inactiveGlobalNavItems = Array.from(document.querySelectorAll('aafp-nav-item[nav-type="global"]')).filter(
        (el: Element) => el.querySelector('.adl-nav-item--inactive'),
      );
      if (!!inactiveGlobalNavItems.length) {
        const navItem: any = inactiveGlobalNavItems[0];
        navItem.closeMobileSubmenu();
      }
    }
    this.mobileMenuIsOpen = !isOpen;
  }
}
